'use client';

import { c as _c } from "react/compiler-runtime";
import { BasicTooltip } from '@/comps/basic-tooltip';
import { ProjectProductBadge } from '@/domains/dashboard/components/project-product-badge';
import { getProjectDisplayReportCount } from '@/domains/pages/utils/project-report-count';
import type { ProjectModel } from '@/models/organizations/internal-models';
import { jetbrainsMono } from '@/theme/utils/font.utils';
import { cn } from '@/utils/shad';
import { Check } from 'lucide-react';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { TSelectItem } from '@/domains/dashboard/components/ProjectSelect';
type ProjectItemProps<TData> = {
  item: TSelectItem<TData>;
  onSelect: (item: TSelectItem<TData>) => void;
  selected?: boolean;
  variant?: 'default' | 'picker';
};
function isProjectModel(data: unknown): data is ProjectModel {
  return typeof data === 'object' && data !== null && 'product' in data && 'nReports' in data && typeof (data as ProjectModel).id === 'string';
}
function PickerItemName(t0) {
  const $ = _c(13);
  const {
    name,
    selected
  } = t0;
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const el = textRef.current;
      if (!el) {
        return;
      }
      const measure = () => {
        const next = el.scrollWidth > el.clientWidth;
        setIsTruncated(prev => prev === next ? prev : next);
      };
      measure();
      const resizeObserver = new ResizeObserver(measure);
      resizeObserver.observe(el);
      return () => resizeObserver.disconnect();
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isTruncated || $[2] !== name) {
    t2 = [name, isTruncated];
    $[1] = isTruncated;
    $[2] = name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useLayoutEffect(t1, t2);
  const t3 = selected ? "font-semibold text-accent-foreground" : "font-normal text-zinc-400";
  let t4;
  if ($[4] !== t3) {
    t4 = cn("min-w-0 flex-1 truncate text-[11px]", t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== name || $[7] !== t4) {
    t5 = <span ref={textRef} className={t4}>{name}</span>;
    $[6] = name;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== isTruncated || $[10] !== name || $[11] !== t5) {
    t6 = <BasicTooltip text={name} show={isTruncated} side="right" classNameContent="max-w-sm wrap-break-word text-start">{t5}</BasicTooltip>;
    $[9] = isTruncated;
    $[10] = name;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
function ProjectItem(t0) {
  const $ = _c(46);
  const {
    item,
    onSelect,
    selected,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const project = isProjectModel(item.rawData) ? item.rawData : null;
  let t2;
  if ($[0] !== project) {
    t2 = project ? getProjectDisplayReportCount(project) : 0;
    $[0] = project;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const reportCount = t2;
  if (variant === "picker") {
    let t3;
    if ($[2] !== item || $[3] !== onSelect) {
      t3 = () => onSelect(item);
      $[2] = item;
      $[3] = onSelect;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    const t4 = selected ? "true" : undefined;
    const t5 = selected && "bg-accent text-accent-foreground ring-1 ring-white/15 ring-inset hover:bg-accent";
    let t6;
    if ($[5] !== t5) {
      t6 = cn("group relative flex w-full min-w-0 cursor-pointer flex-col gap-0.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent hover:ring-1 hover:ring-white/15 hover:ring-inset", t5);
      $[5] = t5;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    let t7;
    if ($[7] !== item.icon) {
      t7 = item.icon ? <span className="inline-flex shrink-0">{item.icon}</span> : null;
      $[7] = item.icon;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    let t8;
    if ($[9] !== item.name || $[10] !== selected) {
      t8 = <PickerItemName name={item.name} selected={selected} />;
      $[9] = item.name;
      $[10] = selected;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    let t9;
    if ($[12] !== selected) {
      t9 = selected ? <Check size={12} className="ml-auto shrink-0 text-accent-foreground" strokeWidth={2.4} aria-hidden={true} /> : null;
      $[12] = selected;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    let t10;
    if ($[14] !== t7 || $[15] !== t8 || $[16] !== t9) {
      t10 = <span className="flex min-w-0 items-center gap-2">{t7}{t8}{t9}</span>;
      $[14] = t7;
      $[15] = t8;
      $[16] = t9;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    let t11;
    if ($[18] !== project || $[19] !== reportCount || $[20] !== selected) {
      t11 = project ? <span className={cn(jetbrainsMono.className, "flex items-center gap-3 text-[10px] whitespace-nowrap", selected ? "text-zinc-300" : "text-zinc-600")}><ProjectProductBadge product={project.product} showIcon={false} className={cn("px-1.5 py-0 text-[9px]", selected ? "border-white/25 text-zinc-200" : "group-hover:border-white/25")} /><span className="leading-none">{reportCount} {reportCount === 1 ? "report" : "reports"}</span></span> : null;
      $[18] = project;
      $[19] = reportCount;
      $[20] = selected;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    let t12;
    if ($[22] !== t10 || $[23] !== t11 || $[24] !== t3 || $[25] !== t4 || $[26] !== t6) {
      t12 = <button type="button" onClick={t3} aria-current={t4} className={t6}>{t10}{t11}</button>;
      $[22] = t10;
      $[23] = t11;
      $[24] = t3;
      $[25] = t4;
      $[26] = t6;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    return t12;
  }
  let t3;
  if ($[28] !== item || $[29] !== onSelect) {
    t3 = () => onSelect(item);
    $[28] = item;
    $[29] = onSelect;
    $[30] = t3;
  } else {
    t3 = $[30];
  }
  const t4 = item.name;
  const t5 = item.icon;
  const t6 = selected ? "font-semibold" : "text-neutral-400";
  let t7;
  if ($[31] !== t6) {
    t7 = cn("max-w-38.25 min-w-0 truncate", t6);
    $[31] = t6;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== item.name || $[34] !== t7) {
    t8 = <p className={t7}>{item.name}</p>;
    $[33] = item.name;
    $[34] = t7;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== item.icon || $[37] !== t8) {
    t9 = <div className="flex items-center gap-1.5">{t5}{t8}</div>;
    $[36] = item.icon;
    $[37] = t8;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== selected) {
    t10 = selected && <Check size={12} className="text-primary-500" />;
    $[39] = selected;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== item.name || $[42] !== t10 || $[43] !== t3 || $[44] !== t9) {
    t11 = <button type="button" onClick={t3} className="flex w-full items-center gap-3 rounded-lg px-1.5 py-0.5 text-[11px] hover:cursor-pointer hover:bg-neutral-800" title={t4}>{t9}{t10}</button>;
    $[41] = item.name;
    $[42] = t10;
    $[43] = t3;
    $[44] = t9;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  return t11;
}
function isItemEqual<TData>(prevProps: ProjectItemProps<TData>, nextProps: ProjectItemProps<TData>) {
  if (prevProps.item.id !== nextProps.item.id || prevProps.selected !== nextProps.selected) {
    return false;
  }
  if (prevProps.variant !== nextProps.variant) {
    return false;
  }
  const prevProject = isProjectModel(prevProps.item.rawData) ? prevProps.item.rawData : null;
  const nextProject = isProjectModel(nextProps.item.rawData) ? nextProps.item.rawData : null;
  if (prevProject === null && nextProject === null) {
    return true;
  }
  if (prevProject === null || nextProject === null) {
    return false;
  }
  return prevProject.product === nextProject.product && prevProject.nReports === nextProject.nReports && prevProject.enabledSubproducts.join(',') === nextProject.enabledSubproducts.join(',');
}
export const MemoizedProjectItem = React.memo(ProjectItem, isItemEqual);